import { useEffect, useMemo, useState } from 'react';
import { selectChartFilterByWidgetId } from '../store/slices/chart-filters.slice';
import { RootState } from 'common/store';
import { useMediaQuery } from 'react-responsive';
import { useResize } from './resize';
import { REPORTS_ROUTES } from 'routes/routes.const';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { useSelector } from 'react-redux';
import Color from 'color';

interface UseChartColorsArgs {
  widgetId: string;
  colorPalette: string[];
  datasetId: string;
  seriesValues?: string[];
}

export function useChartColors({
  widgetId,
  colorPalette,
  datasetId,
  seriesValues,
}: UseChartColorsArgs) {
  const { compareUrl } = useCompareUrlWithProjectId();
  const isReportPage = compareUrl(REPORTS_ROUTES.root, { includes: true });
  const chartFilters = useSelector((state: RootState) =>
    selectChartFilterByWidgetId(state, datasetId, widgetId)
  );
  const chartFilterValue = chartFilters?.map((filter) => filter.value as string);
  const selectedSeriesIndex =
    seriesValues?.findIndex((seriesItem) => chartFilterValue?.includes(seriesItem)) ?? -1;

  return useMemo(() => {
    if (selectedSeriesIndex === -1) {
      return colorPalette;
    }

    return colorPalette.map((color: string, index: number) =>
      isReportPage || index === selectedSeriesIndex ? color : Color(color).lighten(0.5).hex()
    );
  }, [colorPalette, selectedSeriesIndex, isReportPage]);
}

export function useGetDonutHoleSize() {
  const DESKTOP_HOLE_SIZE = useMemo(() => 90, []);
  const MOBILE_HOLE_SIZE = useMemo(() => 70, []);
  const { isTabletOrMobile } = useResize();
  const [holeSize, setHoleSize] = useState<number>(DESKTOP_HOLE_SIZE);

  useEffect(() => {
    if (isTabletOrMobile) {
      setHoleSize(MOBILE_HOLE_SIZE);
    } else {
      setHoleSize(DESKTOP_HOLE_SIZE);
    }
  }, [isTabletOrMobile, DESKTOP_HOLE_SIZE, MOBILE_HOLE_SIZE]);

  return { holeSize };
}

export function useGetResizableCulmnSize() {
  const [resizableCulmnSize, setResizableCulmnSize] = useState<string | undefined>(undefined);

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });

  useEffect(() => {
    if (isTabletOrMobile) {
      setResizableCulmnSize('140px');
    } else {
      setResizableCulmnSize(undefined);
    }
  }, [isTabletOrMobile]);

  return { resizableCulmnSize };
}
